import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, X, CheckCircle2, AlertTriangle, Download, ChevronLeft, Edit3, Trash2, ArrowUp, ArrowDown, Plus, Upload } from 'lucide-react';
import StepMaterialSheet from './ProjectMaterialSheet';
import PageHeader from '../components/PageHeader';
import AIJobProgress from '../components/AIJobProgress';
import { apiGet, apiPost, apiPut, apiDel, apiUpload, api } from '../lib/api';
import { toast } from 'sonner';
import DocumentUploader, { PROJECT_PLANNING_DOC_CATEGORIES } from '../components/DocumentUploader';

/* Excel upload — alternative to AI generation (Change 1C). Used for both
   block and micro schedules. After upload, the normal review/approve UI shows. */
function ScheduleUploader({ planId, kind, reload, setWorking }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const isBlock = kind === 'block';
  const endpoint = isBlock ? 'upload-block-schedule' : 'upload-micro-schedule';
  const label = isBlock ? 'Block Schedule' : 'Micro Schedule';
  const templateUrl = `${process.env.REACT_APP_BACKEND_URL}/api/project-planning/templates/${kind}-schedule.xlsx`;
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setWorking?.(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await apiUpload(`/project-planning/plans/${planId}/${endpoint}`, fd);
      toast.success(`Uploaded ${isBlock ? r.block_count : r.task_count} ${isBlock ? 'blocks' : 'tasks'} — review below`);
      reload();
    } catch (e2) {
      const det = e2?.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : (det?.message || `Upload failed`));
    } finally {
      setBusy(false); setWorking?.(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`pp-upload-${kind}-btn`}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-display font-semibold rounded-sm hover:border-[#3b4872] hover:text-[#3b4872] disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload {label}
      </button>
      <a href={templateUrl} data-testid={`pp-template-${kind}-link`}
        className="text-xs text-[#3b4872] underline hover:no-underline">
        Not sure of the format? Download sample template →
      </a>
      <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={onPick} data-testid={`pp-upload-${kind}-input`} />
    </div>
  );
}

const STATUS_LABEL = {
  pending: 'Pending', generated: 'Awaiting Planner', planner_approved: 'Awaiting PM',
  pm_approved: 'Awaiting MD/CEO', md_approved: 'Fully Approved', rejected: 'Rejected',
};
const STATUS_COLOR = {
  pending: 'bg-slate-100 text-slate-700',
  generated: 'bg-indigo-50 text-indigo-700',
  planner_approved: 'bg-sky-50 text-sky-700',
  pm_approved: 'bg-amber-50 text-amber-800',
  md_approved: 'bg-emerald-50 text-emerald-800',
  rejected: 'bg-rose-50 text-rose-700',
};

const ElloraAI = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-display font-semibold text-[#3b4872] bg-indigo-50 border border-indigo-100 rounded-sm px-1.5 py-0.5" data-testid="ellora-ai-badge">
    <Sparkles size={10} /> Powered by Ellora AI
  </span>
);

export default function ProjectPlanningDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [activeStep, setActiveStep] = useState('documents');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    try {
      setPlan(await apiGet(`/project-planning/plans/${id}`));
      setLoadError(null);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || 'Failed to load this plan — you may not have access to it.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const handleDeletePlan = async () => {
    setDeleting(true);
    try {
      await apiDel(`/project-planning/plans/${id}`);
      toast.success('Plan deleted');
      navigate('/app/project-planning');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete plan');
      setDeleting(false);
    }
  };

  const isV2 = (plan?.machine_version || 1) >= 2;
  const flow = plan?.flow_type || 'item_rate';

  useEffect(() => {
    if (!plan) return;
    const map = {
      documents: 'documents', wbs: 'wbs', boq_draft: 'boq_draft',
      block_schedule: 'block', micro_schedule: 'micro',
      material_sheet: 'material_sheet',
      completed: isV2 ? 'material_sheet' : 'finalize',
    };
    setActiveStep(map[plan.phase] || 'documents');
  }, [plan?.phase]);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading plan…</div>;

  if (loadError || !plan) {
    return (
      <div className="p-10 text-center" data-testid="pp-detail-error">
        <AlertTriangle size={28} className="mx-auto text-rose-400 mb-3" />
        <div className="text-sm text-rose-600 font-display font-semibold max-w-md mx-auto">
          {typeof loadError === 'string' ? loadError : 'You do not have access to this plan, or it no longer exists.'}
        </div>
        <button onClick={() => navigate('/app/project-planning')} data-testid="pp-detail-error-back"
          className="mt-4 text-xs text-slate-500 hover:text-[#3b4872] underline">
          ← Back to all plans
        </button>
      </div>
    );
  }

  const stepDefs = isV2
    ? (flow === 'design_build'
      ? [
          { key: 'documents', label: '1. Documents & Prompt' },
          { key: 'wbs', label: '2. WBS' },
          { key: 'boq_draft', label: '3. Draft BOQ' },
          { key: 'block', label: '4. Block Schedule' },
          { key: 'micro', label: '5. Micro Schedule' },
          { key: 'finalize', label: '6. Publish Schedule to WBS' },
          { key: 'material_sheet', label: '7. Material Sheet' },
        ]
      : [
          { key: 'documents', label: '1. Documents & Prompt' },
          { key: 'wbs', label: '2. WBS' },
          { key: 'block', label: '3. Block Schedule' },
          { key: 'micro', label: '4. Micro Schedule' },
          { key: 'finalize', label: '5. Publish Schedule to WBS' },
          { key: 'material_sheet', label: '6. Material Sheet' },
        ])
    : [
        { key: 'documents', label: '1. Documents & Prompt' },
        { key: 'block', label: '2. Block Schedule' },
        { key: 'micro', label: '3. Micro Schedule' },
        { key: 'finalize', label: '4. Publish Schedule to WBS' },
      ];

  const flowLabel = flow === 'design_build'
    ? 'Design & Build · WBS → BOQ → Block → Micro'
    : 'Item Rate · BOQ → WBS → Block → Micro';

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="pp-detail">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate('/app/project-planning')} className="text-xs font-display font-semibold text-slate-500 hover:text-[#3b4872] flex items-center gap-1">
          <ChevronLeft size={13} /> Back to all plans
        </button>
        <button
          onClick={() => setConfirmDelete(true)}
          data-testid="pp-delete-plan-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-semibold text-rose-600 border border-rose-200 rounded-sm hover:bg-rose-50 transition-colors"
          title="Delete this plan"
        >
          <Trash2 size={13} /> Delete Plan
        </button>
      </div>
      <PageHeader
        overline={`Plan · ${plan.project_code || plan.project_id?.slice(0, 8)}`}
        title={plan.title || 'Project Plan'}
        subtitle={`Created ${plan.created_at?.slice(0,10)} by ${plan.created_by} · Phase: ${plan.phase?.replace('_',' ').toUpperCase()}`}
      />
      {isV2 && (
        <div className="flex items-center gap-2 -mt-3 mb-4">
          <span className="px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest bg-[#3b4872] text-white" data-testid="pp-flow-badge">{flowLabel}</span>
          <ElloraAI />
        </div>
      )}

      {/* Stepper — rendered from the plan's own flow sequence */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto" data-testid="pp-stepper">
        {stepDefs.map(s => (
          <button key={s.key} onClick={() => setActiveStep(s.key)} data-testid={`pp-step-${s.key}`}
            className={`px-4 py-2.5 text-xs font-display font-bold uppercase tracking-widest border-b-2 whitespace-nowrap ${activeStep === s.key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {activeStep === 'documents' && <StepDocuments plan={plan} reload={load} setWorking={setWorking} working={working} />}
      {activeStep === 'wbs' && <StepWBS plan={plan} reload={load} setWorking={setWorking} working={working} />}
      {activeStep === 'boq_draft' && <StepBOQDraft plan={plan} reload={load} setWorking={setWorking} working={working} />}
      {activeStep === 'block' && <StepBlock plan={plan} reload={load} setWorking={setWorking} working={working} />}
      {activeStep === 'micro' && <StepMicro plan={plan} reload={load} setWorking={setWorking} working={working} />}
      {activeStep === 'finalize' && <StepFinalize plan={plan} reload={load} setWorking={setWorking} working={working} />}
      {activeStep === 'material_sheet' && <StepMaterialSheet plan={plan} reload={load} setWorking={setWorking} />}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-rose-600" />
              </div>
              <div>
                <h3 className="font-display font-bold text-base">Delete this plan?</h3>
                <p className="text-xs text-slate-500 mt-0.5">This will permanently remove the plan, WBS, material sheet, and all related data. This cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} disabled={deleting}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleDeletePlan} disabled={deleting}
                data-testid="pp-confirm-delete-plan-btn"
                className="px-4 py-2 text-sm bg-rose-600 text-white rounded-sm font-display font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2">
                {deleting ? 'Deleting…' : 'Yes, delete plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────── STEP 1 — Documents & Prompt ─────────── */
function StepDocuments({ plan, reload, setWorking, working }) {
  const [prompt, setPrompt] = useState(plan.instructions_prompt || '');

  const savePrompt = async () => {
    try { await apiPut(`/project-planning/plans/${plan.id}/instructions`, { instructions_prompt: prompt }); toast.success('Instructions saved'); reload(); } catch { toast.error('Failed'); }
  };

  const [activeJob, setActiveJob] = useState(null);  // { id, kind, title }
  const isV2 = (plan.machine_version || 1) >= 2;

  const generateNext = async () => {
    if (!plan.documents?.length) { toast.error('Upload at least 1 document first'); return; }
    setWorking(true);
    try {
      await savePrompt();
      if (isV2) {
        const r = await apiPost(`/project-planning/plans/${plan.id}/generate-wbs`);
        setActiveJob({ id: r.job_id, kind: 'wbs', title: 'Generating WBS…' });
      } else {
        const r = await apiPost(`/project-planning/plans/${plan.id}/generate-block`);
        setActiveJob({ id: r.job_id, kind: 'block', title: 'Generating block schedule…' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start AI job');
      setWorking(false);
    }
  };

  const onJobComplete = (result) => {
    setActiveJob(null);
    setWorking(false);
    toast.success(`Done · ${result.package_count || result.block_count || result.task_count || 0} items generated`);
    reload();
  };
  const onJobFail = (err) => {
    setActiveJob(null);
    setWorking(false);
    toast.error(err);
  };
  const onJobCancel = () => {
    setActiveJob(null);
    setWorking(false);
  };

  const docs = plan.documents || [];

  return (
    <div className="space-y-5">
      {/* Format warning */}
      <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2.5 rounded-sm text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
        <div>
          <strong>For best AI results, upload:</strong> <u>BOQ as Excel/PDF</u>, <u>Drawings as searchable PDF or DXF</u> (image PDFs are auto-OCR'd · slower), <u>SCC/GCC/Tender as PDF</u>. DWG drawings cannot be parsed — export to DXF first.
        </div>
      </div>

      {/* Shared <DocumentUploader> — same component used by Tendering / BD. */}
      <DocumentUploader
        entityType="project"
        entityId={plan.id}
        documents={docs}
        onChange={() => reload()}
        categories={PROJECT_PLANNING_DOC_CATEGORIES}
        defaultCategory="boq"
        accept=".pdf,.xls,.xlsx,.dxf,.dwg"
        testidPrefix="pp"
        title="1. Add Documents"
      />

      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display font-bold text-base mb-2">2. Instructions for AI (optional but recommended)</h3>
        <p className="text-xs text-slate-500 mb-3">Tell the AI about constraints, priorities, or special site conditions. e.g., "Single shift 6 days/week. Prioritise civil before MEP. Monsoon June-Sep — slow productivity on excavation."</p>
        <textarea data-testid="pp-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} rows={5} maxLength={4000}
          placeholder="Single shift, 6 days/week. Foundation must complete by 15-July before monsoon. Prefer MS welding over bolting for shed frame…"
          className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm font-mono" />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-slate-400">{prompt.length}/4000</span>
          <button onClick={savePrompt} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50">Save Instructions</button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {!isV2 ? <ScheduleUploader planId={plan.id} kind="block" reload={reload} setWorking={setWorking} /> : <ElloraAI />}
        <button onClick={generateNext} disabled={working || !docs.length} data-testid={isV2 ? 'pp-generate-wbs-btn' : 'pp-generate-block-btn'}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] disabled:opacity-50">
          {working ? <><Loader2 size={14} className="animate-spin" /> Working…</> : <><Sparkles size={14} /> {isV2 ? 'Generate WBS with AI' : 'Generate Block Schedule with AI'}</>}
        </button>
      </div>

      {activeJob && (
        <AIJobProgress
          jobId={activeJob.id}
          title={activeJob.title}
          onComplete={onJobComplete}
          onFail={onJobFail}
          onCancel={onJobCancel}
        />
      )}
    </div>
  );
}


/* ─────────── STEP 2 — Block Schedule ─────────── */
function StepBlock({ plan, reload, setWorking, working }) {
  const [blocks, setBlocks] = useState(plan.blocks || []);
  const [editing, setEditing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [activeJob, setActiveJob] = useState(null);

  useEffect(() => { setBlocks(plan.blocks || []); }, [plan.blocks]);

  const isV2 = (plan.machine_version || 1) >= 2;
  if (isV2 && plan.wbs_status !== 'md_approved') {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm" data-testid="pp-block-gate-wbs">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">WBS must be fully approved first</div>
      <p className="text-sm text-slate-500">Complete the 3-stage approval (Planner → PM → MD) on the WBS step to unlock block-schedule generation.</p>
    </div>;
  }
  if (isV2 && plan.flow_type === 'design_build' && plan.boq_draft_status !== 'md_approved') {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm" data-testid="pp-block-gate-boq">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">Draft BOQ must be MD-approved first</div>
      <p className="text-sm text-slate-500">In the Design & Build flow, the AI-derived BOQ must be approved by MD (Draft BOQ step) before block-schedule generation.</p>
    </div>;
  }

  if (!blocks.length) {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No block schedule yet</div>
      {isV2 ? (
        <>
          <p className="text-sm text-slate-500 mb-4">Generate the block schedule from the approved WBS — or upload your own from Excel.</p>
          <GenerateBlockButton plan={plan} reload={reload} setWorking={setWorking} />
        </>
      ) : (
        <p className="text-sm text-slate-500 mb-4">Go to Step 1 and generate with AI — or upload your own block schedule from Excel.</p>
      )}
      <div className="flex justify-center mt-4">
        <ScheduleUploader planId={plan.id} kind="block" reload={reload} setWorking={setWorking} />
      </div>
    </div>;
  }

  const save = async () => {
    setWorking(true);
    try { await apiPut(`/project-planning/plans/${plan.id}/blocks`, { blocks }); toast.success('Saved'); setEditing(false); reload(); } catch { toast.error('Save failed'); }
    setWorking(false);
  };
  const regen = async () => {
    if (!confirm('Re-generate the block schedule from documents? Current edits will be lost.')) return;
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/generate-block`);
      setActiveJob({ id: r.job_id, title: 'Re-generating block schedule…' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setWorking(false); }
  };
  const approve = async () => {
    setWorking(true);
    try { const r = await apiPost(`/project-planning/plans/${plan.id}/blocks/approve`); toast.success(`Status → ${r.block_status}`); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Forbidden'); }
    setWorking(false);
  };
  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setWorking(true);
    try { await apiPost(`/project-planning/plans/${plan.id}/blocks/reject`, { reason: rejectReason }); toast.success('Rejected — back to Step 1'); setShowReject(false); setRejectReason(''); reload(); } catch { toast.error('Failed'); }
    setWorking(false);
  };

  // Full editing helpers
  const updateBlock = (idx, key, val) => setBlocks(bs => bs.map((b, i) => i === idx ? { ...b, [key]: val } : b));
  const moveBlock = (idx, dir) => setBlocks(bs => {
    const next = [...bs]; const t = idx + dir;
    if (t < 0 || t >= next.length) return next;
    [next[idx], next[t]] = [next[t], next[idx]];
    return next;
  });
  const addBlock = () => setBlocks(bs => [...bs, {
    id: `new-${Date.now()}`, code: `${bs.length + 1}`, name: 'New phase',
    start_date: plan.blocks?.[0]?.start_date || '', end_date: '', duration_days: 0,
    weightage_pct: 0, dependencies: [], key_activities: [], boq_refs: [], remarks: '',
  }]);
  const removeBlock = (idx) => setBlocks(bs => bs.filter((_, i) => i !== idx));

  const status = plan.block_status;
  const isFinalApproved = status === 'md_approved';

  return (
    <div className="space-y-4">
      <StatusBar stage="Block" status={status} approvals={(plan.approvals || []).filter(a => a.stage === 'block')} reason={plan.block_rejection_reason} />

      {/* Action bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/project-planning/plans/${plan.id}/block-schedule.xlsx`}
             target="_blank" rel="noreferrer" data-testid="pp-block-xlsx"
             className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Download size={12} /> Excel</a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/project-planning/plans/${plan.id}/block-schedule.pdf`}
             target="_blank" rel="noreferrer" data-testid="pp-block-pdf"
             className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Download size={12} /> PDF</a>
          {!isFinalApproved && (
            <button onClick={() => setEditing(e => !e)} data-testid="pp-block-edit-btn"
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Edit3 size={12} /> {editing ? 'Cancel Edit' : 'Edit All Fields'}</button>
          )}
          {editing && <>
            <button onClick={addBlock} className="px-3 py-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-sm hover:bg-emerald-50 flex items-center gap-1"><Plus size={12} /> Add Block</button>
            <button onClick={save} data-testid="pp-block-save-btn" className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm">Save Edits</button>
          </>}
          {!isFinalApproved && !editing && (
            <button onClick={regen} className="px-3 py-1.5 text-xs border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50 flex items-center gap-1"><Sparkles size={12} /> Re-generate</button>
          )}
        </div>
        {!isFinalApproved && status !== 'rejected' && (
          <div className="flex gap-2">
            <button onClick={() => setShowReject(true)} data-testid="pp-block-reject-btn" className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50">Reject</button>
            <button onClick={approve} disabled={working} data-testid="pp-block-approve-btn" className="px-4 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-semibold">Approve Next Stage</button>
          </div>
        )}
      </div>

      {activeJob && (
        <AIJobProgress jobId={activeJob.id} title={activeJob.title}
          onComplete={() => { setActiveJob(null); setWorking(false); toast.success('Re-generated'); reload(); }}
          onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
          onCancel={() => { setActiveJob(null); setWorking(false); }} />
      )}

      {/* Table */}
      <BlockTable blocks={blocks} editing={editing} update={updateBlock} move={moveBlock} remove={removeBlock} />

      {/* Gantt */}
      <GanttView items={blocks} title="Block Schedule (Gantt)" editable={editing} onChange={(idx, patch) => setBlocks(bs => bs.map((b, i) => i === idx ? { ...b, ...patch } : b))} />

      {plan.block_narrative && (
        <div className="bg-slate-50 border-l-2 border-l-[#3b4872] p-3 text-sm">
          <div className="kpi-overline mb-1 text-[#3b4872]">AI Narrative</div>
          <p>{plan.block_narrative}</p>
        </div>
      )}
      {showReject && (
        <RejectModal reason={rejectReason} setReason={setRejectReason} onCancel={() => setShowReject(false)} onSubmit={reject} />
      )}
    </div>
  );
}


/* ─────────── STEP 3 — Micro Schedule ─────────── */
function StepMicro({ plan, reload, setWorking, working }) {
  const [tasks, setTasks] = useState(plan.micro_tasks || []);
  const [editing, setEditing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  useEffect(() => { setTasks(plan.micro_tasks || []); }, [plan.micro_tasks]);

  if (plan.block_status !== 'md_approved') {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">Block schedule must be fully approved first</div>
      <p className="text-sm text-slate-500">Complete the 3-stage approval (Planner → PM → MD) on Step 2 to unlock micro-schedule generation.</p>
    </div>;
  }

  const generate = async (force = false) => {
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/generate-micro${force ? '?force=true' : ''}`);
      const title = force
        ? 'Re-generating ALL blocks from scratch…'
        : 'Generating day-wise micro schedule (resuming where left off)…';
      setActiveJob({ id: r.job_id, title });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setWorking(false); }
  };
  const save = async () => {
    setWorking(true);
    try { await apiPut(`/project-planning/plans/${plan.id}/micro-tasks`, { micro_tasks: tasks }); toast.success('Saved'); setEditing(false); reload(); } catch { toast.error('Save failed'); }
    setWorking(false);
  };
  const approve = async () => {
    setWorking(true);
    try { const r = await apiPost(`/project-planning/plans/${plan.id}/micro/approve`); toast.success(`Status → ${r.micro_status}`); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Forbidden'); }
    setWorking(false);
  };
  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setWorking(true);
    try { await apiPost(`/project-planning/plans/${plan.id}/micro/reject`, { reason: rejectReason }); toast.success('Rejected — back to Step 2'); setShowReject(false); setRejectReason(''); reload(); } catch { toast.error('Failed'); }
    setWorking(false);
  };

  // Editing helpers
  const updateTask = (idx, key, val) => setTasks(ts => ts.map((t, i) => i === idx ? { ...t, [key]: val } : t));
  const moveTask = (idx, dir) => setTasks(ts => {
    const next = [...ts]; const t = idx + dir;
    if (t < 0 || t >= next.length) return next;
    [next[idx], next[t]] = [next[t], next[idx]];
    return next;
  });
  const addTask = (blockCode) => setTasks(ts => [...ts, {
    id: `new-${Date.now()}`, code: '', block_code: blockCode || plan.blocks?.[0]?.code || '',
    name: 'New task', start_date: '', end_date: '', duration_days: 1,
    resources: '', boq_ref: null, category: '', dependencies: [], remarks: '',
  }]);
  const removeTask = (idx) => setTasks(ts => ts.filter((_, i) => i !== idx));

  if (!tasks.length && !activeJob) {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No micro schedule yet</div>
      <p className="text-xs text-slate-400 mt-2 mb-3 max-w-md mx-auto">AI will produce 8-20 detailed day-wise tasks per block, including often-missed activities like backfilling, curing, anti-termite, formwork stripping, snag closure.</p>
      <button onClick={() => generate(false)} disabled={working} data-testid="pp-generate-micro-btn"
        className="flex items-center gap-2 mx-auto mt-3 px-5 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] disabled:opacity-50">
        {working ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <><Sparkles size={14} /> Generate Day-wise Micro Schedule</>}
      </button>
      <div className="flex justify-center mt-4 pt-4 border-t border-slate-100">
        <ScheduleUploader planId={plan.id} kind="micro" reload={reload} setWorking={setWorking} />
      </div>
    </div>;
  }
  if (activeJob && !tasks.length) {
    return <AIJobProgress jobId={activeJob.id} title={activeJob.title}
      onComplete={() => { setActiveJob(null); setWorking(false); reload(); }}
      onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
      onCancel={() => { setActiveJob(null); setWorking(false); }} />;
  }

  const status = plan.micro_status;
  const isFinal = status === 'md_approved';

  return (
    <div className="space-y-4">
      <StatusBar stage="Micro" status={status} approvals={(plan.approvals || []).filter(a => a.stage === 'micro')} reason={plan.micro_rejection_reason} />

      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/project-planning/plans/${plan.id}/micro-schedule.xlsx`}
             target="_blank" rel="noreferrer" data-testid="pp-micro-xlsx"
             className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Download size={12} /> Excel</a>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/project-planning/plans/${plan.id}/micro-schedule.pdf`}
             target="_blank" rel="noreferrer" data-testid="pp-micro-pdf"
             className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Download size={12} /> PDF</a>
          {!isFinal && (
            <button onClick={() => setEditing(e => !e)} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Edit3 size={12} /> {editing ? 'Cancel' : 'Edit All Fields'}</button>
          )}
          {editing && <>
            <button onClick={() => addTask()} className="px-3 py-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-sm hover:bg-emerald-50 flex items-center gap-1"><Plus size={12} /> Add Task</button>
            <button onClick={save} className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm">Save Edits</button>
          </>}
          {!isFinal && !editing && (
            <>
              <button onClick={() => generate(false)} data-testid="pp-micro-resume-btn" className="px-3 py-1.5 text-xs border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50 flex items-center gap-1" title="Skip blocks that already have tasks · only generate missing blocks">
                <Sparkles size={12} /> Resume / Generate Missing
              </button>
              <button
                onClick={() => { if (window.confirm('Discard ALL existing micro-tasks and regenerate every block from scratch? This will cost more AI credits.')) generate(true); }}
                data-testid="pp-micro-regenerate-btn"
                className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50 flex items-center gap-1"
                title="Wipe and regenerate ALL blocks">
                <Sparkles size={12} /> Re-generate All
              </button>
            </>
          )}
        </div>
        {!isFinal && status !== 'rejected' && (
          <div className="flex gap-2">
            <button onClick={() => setShowReject(true)} data-testid="pp-micro-reject-btn" className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50">Reject</button>
            <button onClick={approve} disabled={working} data-testid="pp-micro-approve-btn" className="px-4 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-semibold">Approve Next Stage</button>
          </div>
        )}
      </div>

      {activeJob && (
        <AIJobProgress jobId={activeJob.id} title={activeJob.title}
          onComplete={() => { setActiveJob(null); setWorking(false); reload(); }}
          onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
          onCancel={() => { setActiveJob(null); setWorking(false); }} />
      )}

      <MicroTable tasks={tasks} blocks={plan.blocks} editing={editing} update={updateTask} move={moveTask} remove={removeTask} />
      <GanttView items={tasks} title="Micro Schedule (Gantt)" editable={editing} onChange={(idx, patch) => setTasks(ts => ts.map((t, i) => i === idx ? { ...t, ...patch } : t))} />

      {showReject && <RejectModal reason={rejectReason} setReason={setRejectReason} onCancel={() => setShowReject(false)} onSubmit={reject} />}
    </div>
  );
}


/* ─────────── STEP 4 — Finalise WBS ─────────── */
function StepFinalize({ plan, reload, setWorking, working }) {
  if (plan.micro_status !== 'md_approved' && plan.phase !== 'completed') {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">Approve the Micro schedule first</div>
      <p className="text-sm text-slate-500">Complete the 3-stage approval (Planner → PM → MD) on Step 3 to enable WBS finalisation.</p>
    </div>;
  }
  const finalize = async () => {
    if (!confirm('This will write the approved blocks + tasks into the project WBS. Continue?')) return;
    setWorking(true);
    try { const r = await apiPost(`/project-planning/plans/${plan.id}/finalize-wbs`); toast.success(`WBS finalised · ${r.wbs_rows_created} rows`); reload(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setWorking(false);
  };
  if (plan.phase === 'completed') {
    return <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center">
      <CheckCircle2 size={32} className="mx-auto text-emerald-600 mb-2" />
      <div className="font-display font-bold text-lg">WBS finalised</div>
      <p className="text-sm text-emerald-800 mt-1">{plan.blocks?.length || 0} blocks + {plan.micro_tasks?.length || 0} micro-tasks written into the project WBS on {plan.finalised_at?.slice(0,10)} by {plan.finalised_by}.</p>
      <a href={`/projects/${plan.project_id}?tab=WBS`} data-testid="pp-open-wbs-link" className="inline-block mt-4 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm">Open WBS →</a>
    </div>;
  }
  return <div className="bg-white border border-slate-200 rounded-sm p-6 text-center">
    <div className="font-display font-bold text-lg">Ready to finalise</div>
    <p className="text-sm text-slate-500 mt-1">Approved · {plan.blocks?.length || 0} blocks + {plan.micro_tasks?.length || 0} micro-tasks. Click below to write into the WBS.</p>
    <button onClick={finalize} disabled={working} data-testid="pp-finalize-btn"
      className="mt-4 px-5 py-2.5 bg-emerald-600 text-white text-sm font-display font-semibold rounded-sm disabled:opacity-50">
      {working ? 'Writing…' : 'Finalise WBS'}
    </button>
  </div>;
}


/* ─────────── v2 helper — generate block from approved WBS ─────────── */
function GenerateBlockButton({ plan, reload, setWorking }) {
  const [job, setJob] = useState(null);
  const start = async () => {
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/generate-block`);
      setJob({ id: r.job_id });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setWorking(false); }
  };
  if (job) return <AIJobProgress jobId={job.id} title="Generating block schedule…"
    onComplete={() => { setJob(null); setWorking(false); reload(); }}
    onFail={(e) => { setJob(null); setWorking(false); toast.error(e); }}
    onCancel={() => { setJob(null); setWorking(false); }} />;
  return (
    <div className="flex flex-col items-center gap-2">
      <button onClick={start} data-testid="pp-generate-block-btn"
        className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658]">
        <Sparkles size={14} /> Generate Block Schedule with AI
      </button>
      <ElloraAI />
    </div>
  );
}


/* ─────────── STEP — WBS (machine_version 2) ─────────── */
function StepWBS({ plan, reload, setWorking, working }) {
  const [pkgs, setPkgs] = useState(plan.wbs_packages || []);
  const [editing, setEditing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  useEffect(() => { setPkgs(plan.wbs_packages || []); }, [plan.wbs_packages]);

  const generate = async () => {
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/generate-wbs`);
      setActiveJob({ id: r.job_id, title: 'Generating WBS…' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setWorking(false); }
  };
  const save = async () => {
    setWorking(true);
    try { await apiPut(`/project-planning/plans/${plan.id}/wbs-packages`, { wbs_packages: pkgs }); toast.success('Saved'); setEditing(false); reload(); } catch { toast.error('Save failed'); }
    setWorking(false);
  };
  const approve = async () => {
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/wbs/approve`);
      toast.success(r.wbs_status === 'md_approved' ? `WBS approved · ${r.wbs_rows_created} rows written to project WBS` : `Status → ${r.wbs_status}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Forbidden'); }
    setWorking(false);
  };
  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setWorking(true);
    try { await apiPost(`/project-planning/plans/${plan.id}/wbs/reject`, { reason: rejectReason }); toast.success('Rejected — back to Documents'); setShowReject(false); setRejectReason(''); reload(); } catch { toast.error('Failed'); }
    setWorking(false);
  };

  const updatePkg = (idx, key, val) => setPkgs(ps => ps.map((p, i) => i === idx ? { ...p, [key]: val } : p));
  const movePkg = (idx, dir) => setPkgs(ps => {
    const next = [...ps]; const t = idx + dir;
    if (t < 0 || t >= next.length) return next;
    [next[idx], next[t]] = [next[t], next[idx]];
    return next;
  });
  const addPkg = () => setPkgs(ps => [...ps, { id: `new-${Date.now()}`, code: `${ps.length + 1}`, parent_code: null, name: 'New package', discipline: '', description: '', boq_refs: [], deliverables: [] }]);
  const removePkg = (idx) => setPkgs(ps => ps.filter((_, i) => i !== idx));

  if (activeJob && !pkgs.length) {
    return <AIJobProgress jobId={activeJob.id} title={activeJob.title}
      onComplete={() => { setActiveJob(null); setWorking(false); reload(); }}
      onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
      onCancel={() => { setActiveJob(null); setWorking(false); }} />;
  }
  if (!pkgs.length) {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm" data-testid="pp-wbs-empty">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No WBS yet</div>
      <p className="text-sm text-slate-500 mb-1 max-w-lg mx-auto">
        {plan.flow_type === 'design_build'
          ? 'AI derives the work breakdown from drawings + tender scope (no BOQ exists yet in this flow).'
          : 'AI derives the work breakdown from the BOQ (the bible), drawings and contract documents.'}
      </p>
      <div className="flex justify-center mb-3"><ElloraAI /></div>
      <button onClick={generate} disabled={working || !plan.documents?.length} data-testid="pp-generate-wbs-btn2"
        className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] disabled:opacity-50">
        {working ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <><Sparkles size={14} /> Generate WBS with AI</>}
      </button>
      {!plan.documents?.length && <p className="text-[11px] text-amber-700 mt-3">Upload documents in Step 1 first.</p>}
    </div>;
  }

  const status = plan.wbs_status;
  const isFinal = status === 'md_approved';

  return (
    <div className="space-y-4" data-testid="pp-wbs-step">
      <StatusBar stage="WBS" status={status} approvals={(plan.approvals || []).filter(a => a.stage === 'wbs')} reason={plan.wbs_rejection_reason} />

      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 items-center">
          <ElloraAI />
          {!isFinal && (
            <button onClick={() => setEditing(e => !e)} data-testid="pp-wbs-edit-btn"
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Edit3 size={12} /> {editing ? 'Cancel Edit' : 'Edit All Fields'}</button>
          )}
          {editing && <>
            <button onClick={addPkg} className="px-3 py-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-sm hover:bg-emerald-50 flex items-center gap-1"><Plus size={12} /> Add Package</button>
            <button onClick={save} data-testid="pp-wbs-save-btn" className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm">Save Edits</button>
          </>}
          {!isFinal && !editing && (
            <button onClick={generate} className="px-3 py-1.5 text-xs border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50 flex items-center gap-1"><Sparkles size={12} /> Re-generate</button>
          )}
        </div>
        {!isFinal && status !== 'rejected' && (
          <div className="flex gap-2">
            <button onClick={() => setShowReject(true)} data-testid="pp-wbs-reject-btn" className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50">Reject</button>
            <button onClick={approve} disabled={working} data-testid="pp-wbs-approve-btn" className="px-4 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-semibold">Approve Next Stage</button>
          </div>
        )}
      </div>

      {activeJob && (
        <AIJobProgress jobId={activeJob.id} title={activeJob.title}
          onComplete={() => { setActiveJob(null); setWorking(false); toast.success('Re-generated'); reload(); }}
          onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
          onCancel={() => { setActiveJob(null); setWorking(false); }} />
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="pp-wbs-table">
        <table className="w-full text-xs">
          <thead className="bg-[#3b4872] text-white">
            <tr>{[editing && '#','Code','Name','Discipline','BOQ Refs','Description','Deliverables', editing && ''].filter(Boolean).map((h,i) => <th key={i} className="text-left px-2 py-1.5 font-display font-bold uppercase tracking-widest text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {pkgs.map((p, i) => (
              <tr key={p.id || i} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                {editing && (
                  <td className="px-1 py-1.5">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={()=>movePkg(i,-1)} disabled={i===0} className="text-slate-400 hover:text-[#3b4872] disabled:opacity-30"><ArrowUp size={11} /></button>
                      <button onClick={()=>movePkg(i,1)} disabled={i===pkgs.length-1} className="text-slate-400 hover:text-[#3b4872] disabled:opacity-30"><ArrowDown size={11} /></button>
                    </div>
                  </td>
                )}
                <td className={`px-2 py-1.5 font-mono tabular-nums ${!p.parent_code ? 'font-bold' : 'pl-5'}`}>{editing ? <input value={p.code||''} onChange={e=>updatePkg(i,'code',e.target.value)} className="w-16 border border-slate-300 rounded-sm px-1 py-0.5" /> : p.code}</td>
                <td className={`px-2 py-1.5 min-w-[200px] ${!p.parent_code ? 'font-display font-bold' : ''}`}>{editing ? <input value={p.name||''} onChange={e=>updatePkg(i,'name',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : p.name}</td>
                <td className="px-2 py-1.5 text-slate-600">{editing ? <input value={p.discipline||''} onChange={e=>updatePkg(i,'discipline',e.target.value)} className="w-24 border border-slate-300 rounded-sm px-1 py-0.5" /> : (p.discipline || '—')}</td>
                <td className="px-2 py-1.5 font-mono text-slate-600 min-w-[140px]">{editing ? <input value={(p.boq_refs||[]).join(', ')} onChange={e=>updatePkg(i,'boq_refs',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} placeholder="comma-separated" className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : ((p.boq_refs||[]).join(', ') || '—')}</td>
                <td className="px-2 py-1.5 text-slate-600 min-w-[220px]">{editing ? <textarea value={p.description||''} onChange={e=>updatePkg(i,'description',e.target.value)} rows={2} className="w-full border border-slate-300 rounded-sm px-1 py-0.5 text-[11px]" /> : (p.description || '—')}</td>
                <td className="px-2 py-1.5 text-slate-600 min-w-[180px]">{editing ? <textarea value={(p.deliverables||[]).join('\n')} onChange={e=>updatePkg(i,'deliverables',e.target.value.split('\n').map(s=>s.trim()).filter(Boolean))} rows={2} placeholder="One per line" className="w-full border border-slate-300 rounded-sm px-1 py-0.5 text-[11px]" /> : ((p.deliverables||[]).join('; ') || '—')}</td>
                {editing && (
                  <td className="px-1 py-1.5">
                    <button onClick={()=>removePkg(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {plan.wbs_narrative && (
        <div className="bg-slate-50 border-l-2 border-l-[#3b4872] p-3 text-sm">
          <div className="kpi-overline mb-1 text-[#3b4872] flex items-center gap-2">AI Narrative <ElloraAI /></div>
          <p>{plan.wbs_narrative}</p>
          {(plan.wbs_assumptions || []).length > 0 && (
            <ul className="mt-2 text-xs text-slate-600 list-disc pl-4">{plan.wbs_assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
          )}
        </div>
      )}
      {showReject && <RejectModal reason={rejectReason} setReason={setRejectReason} onCancel={() => setShowReject(false)} onSubmit={reject} />}
    </div>
  );
}


/* ─────────── STEP — Draft BOQ (design_build flow only) ─────────── */
function StepBOQDraft({ plan, reload, setWorking, working }) {
  const [items, setItems] = useState(plan.boq_draft_items || []);
  const [editing, setEditing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  useEffect(() => { setItems(plan.boq_draft_items || []); }, [plan.boq_draft_items]);

  if (plan.wbs_status !== 'md_approved') {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm" data-testid="pp-boqdraft-gate">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">WBS must be fully approved first</div>
      <p className="text-sm text-slate-500">Complete the 3-stage approval on the WBS step to unlock the draft-BOQ derivation.</p>
    </div>;
  }

  const generate = async () => {
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/generate-boq-draft`);
      setActiveJob({ id: r.job_id, title: 'Deriving quantity take-off from approved WBS…' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); setWorking(false); }
  };
  const save = async () => {
    setWorking(true);
    try { await apiPut(`/project-planning/plans/${plan.id}/boq-draft`, { boq_draft_items: items }); toast.success('Saved'); setEditing(false); reload(); } catch { toast.error('Save failed'); }
    setWorking(false);
  };
  const approve = async () => {
    if (!window.confirm('MD approval will import these items as the project BOQ (source: AI WBS-derived). Continue?')) return;
    setWorking(true);
    try {
      const r = await apiPost(`/project-planning/plans/${plan.id}/boq-draft/approve`);
      toast.success(`Draft BOQ approved · ${r.imported} items imported as project BOQ`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Forbidden'); }
    setWorking(false);
  };
  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Reason required'); return; }
    setWorking(true);
    try { await apiPost(`/project-planning/plans/${plan.id}/boq-draft/reject`, { reason: rejectReason }); toast.success('Rejected — back to WBS'); setShowReject(false); setRejectReason(''); reload(); } catch { toast.error('Failed'); }
    setWorking(false);
  };
  const updateItem = (idx, key, val) => setItems(ts => ts.map((t, i) => i === idx ? { ...t, [key]: val } : t));
  const removeItem = (idx) => setItems(ts => ts.filter((_, i) => i !== idx));

  if (activeJob && !items.length) {
    return <AIJobProgress jobId={activeJob.id} title={activeJob.title}
      onComplete={() => { setActiveJob(null); setWorking(false); reload(); }}
      onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
      onCancel={() => { setActiveJob(null); setWorking(false); }} />;
  }
  if (!items.length) {
    return <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm" data-testid="pp-boqdraft-empty">
      <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No draft BOQ yet</div>
      <p className="text-sm text-slate-500 mb-1 max-w-lg mx-auto">AI derives a quantity take-off BOQ from the approved WBS + drawings. On MD approval it becomes the project BOQ (flagged AI WBS-derived).</p>
      <div className="flex justify-center mb-3"><ElloraAI /></div>
      <button onClick={generate} disabled={working} data-testid="pp-generate-boqdraft-btn"
        className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] disabled:opacity-50">
        {working ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <><Sparkles size={14} /> Derive Draft BOQ with AI</>}
      </button>
    </div>;
  }

  const status = plan.boq_draft_status;
  const isFinal = status === 'md_approved';
  const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const badge = isFinal ? 'bg-emerald-50 text-emerald-800' : status === 'rejected' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800';
  const badgeLabel = isFinal ? 'MD Approved · Imported as Project BOQ' : status === 'rejected' ? 'Rejected' : 'Awaiting MD Approval';

  return (
    <div className="space-y-4" data-testid="pp-boqdraft-step">
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${badge}`} data-testid="pp-boqdraft-status">{badgeLabel}</span>
          <span className="text-xs text-slate-500">{items.length} items · ₹{total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
          <ElloraAI />
        </div>
        <div className="flex gap-2">
          {!isFinal && (
            <>
              <button onClick={() => setEditing(e => !e)} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"><Edit3 size={12} /> {editing ? 'Cancel' : 'Edit'}</button>
              {editing && <button onClick={save} className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm">Save Edits</button>}
              {!editing && <button onClick={generate} className="px-3 py-1.5 text-xs border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50 flex items-center gap-1"><Sparkles size={12} /> Re-derive</button>}
              {status === 'generated' && (
                <>
                  <button onClick={() => setShowReject(true)} data-testid="pp-boqdraft-reject-btn" className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50">Reject</button>
                  <button onClick={approve} disabled={working} data-testid="pp-boqdraft-approve-btn" className="px-4 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-semibold">MD Approve → Import as BOQ</button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {activeJob && (
        <AIJobProgress jobId={activeJob.id} title={activeJob.title}
          onComplete={() => { setActiveJob(null); setWorking(false); reload(); }}
          onFail={(e) => { setActiveJob(null); setWorking(false); toast.error(e); }}
          onCancel={() => { setActiveJob(null); setWorking(false); }} />
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto max-h-[560px] overflow-y-auto" data-testid="pp-boqdraft-table">
        <table className="w-full text-xs">
          <thead className="bg-[#3b4872] text-white sticky top-0">
            <tr>{['Section','Code','Description','Unit','Qty','Rate (₹)','Amount (₹)','WBS Ref','Remarks', editing && ''].filter(Boolean).map((h,i) => <th key={i} className="text-left px-2 py-1.5 font-display font-bold uppercase tracking-widest text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id || i} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                <td className="px-2 py-1.5 text-slate-600 min-w-[110px]">{editing ? <input value={it.section||''} onChange={e=>updateItem(i,'section',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : it.section}</td>
                <td className="px-2 py-1.5 font-mono">{editing ? <input value={it.code||''} onChange={e=>updateItem(i,'code',e.target.value)} className="w-20 border border-slate-300 rounded-sm px-1 py-0.5" /> : it.code}</td>
                <td className="px-2 py-1.5 min-w-[260px]">{editing ? <textarea value={it.description||''} onChange={e=>updateItem(i,'description',e.target.value)} rows={2} className="w-full border border-slate-300 rounded-sm px-1 py-0.5 text-[11px]" /> : it.description}</td>
                <td className="px-2 py-1.5">{editing ? <input value={it.unit||''} onChange={e=>updateItem(i,'unit',e.target.value)} className="w-14 border border-slate-300 rounded-sm px-1 py-0.5" /> : it.unit}</td>
                <td className="px-2 py-1.5 font-mono tabular-nums text-right">{editing ? <input type="number" value={it.quantity||0} onChange={e=>updateItem(i,'quantity',Number(e.target.value))} className="w-20 border border-slate-300 rounded-sm px-1 py-0.5 tabular-nums" /> : Number(it.quantity||0).toLocaleString('en-IN')}</td>
                <td className="px-2 py-1.5 font-mono tabular-nums text-right">{editing ? <input type="number" value={it.rate||0} onChange={e=>updateItem(i,'rate',Number(e.target.value))} className="w-20 border border-slate-300 rounded-sm px-1 py-0.5 tabular-nums" /> : Number(it.rate||0).toLocaleString('en-IN')}</td>
                <td className="px-2 py-1.5 font-mono tabular-nums text-right">{Number((editing ? (it.quantity||0)*(it.rate||0) : it.amount)||0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                <td className="px-2 py-1.5 font-mono">{editing ? <input value={it.wbs_ref||''} onChange={e=>updateItem(i,'wbs_ref',e.target.value)} className="w-14 border border-slate-300 rounded-sm px-1 py-0.5" /> : (it.wbs_ref || '—')}</td>
                <td className="px-2 py-1.5 text-slate-600 min-w-[160px]">{editing ? <input value={it.remarks||''} onChange={e=>updateItem(i,'remarks',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : (it.remarks || '—')}</td>
                {editing && <td className="px-1 py-1.5"><button onClick={()=>removeItem(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {((plan.boq_draft_assumptions || []).length > 0 || (plan.boq_draft_exclusions || []).length > 0) && (
        <div className="bg-slate-50 border-l-2 border-l-[#3b4872] p-3 text-xs">
          <div className="kpi-overline mb-1 text-[#3b4872] flex items-center gap-2">Assumptions & Exclusions <ElloraAI /></div>
          {(plan.boq_draft_assumptions || []).map((a, i) => <div key={`a${i}`}>• {a}</div>)}
          {(plan.boq_draft_exclusions || []).map((x, i) => <div key={`x${i}`} className="text-rose-700">✕ {x}</div>)}
        </div>
      )}
      {showReject && <RejectModal reason={rejectReason} setReason={setRejectReason} onCancel={() => setShowReject(false)} onSubmit={reject} />}
    </div>
  );
}




/* ─────────── Reusable parts ─────────── */
function StatusBar({ stage, status, approvals, reason }) {
  const STEPS = [
    { v: 'generated', label: 'Generated' },
    { v: 'planner_approved', label: 'Planner ✓' },
    { v: 'pm_approved', label: 'PM ✓' },
    { v: 'md_approved', label: 'MD ✓' },
  ];
  const idx = STEPS.findIndex(s => s.v === status);
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`pp-${stage.toLowerCase()}-status`}>
      <div className="flex items-center justify-between mb-2">
        <div className="kpi-overline">{stage} schedule status</div>
        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${STATUS_COLOR[status] || 'bg-slate-100'}`}>{STATUS_LABEL[status] || status}</span>
      </div>
      {status !== 'rejected' && (
        <div className="flex items-center gap-1">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.v}>
              <div className={`flex-1 h-1.5 rounded-sm ${i <= idx ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              <span className={`text-[10px] font-display font-bold ${i <= idx ? 'text-emerald-700' : 'text-slate-400'}`}>{s.label}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {status === 'rejected' && reason && (
        <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-2 py-1.5">Rejected: {reason}</div>
      )}
      {approvals?.length > 0 && (
        <details className="mt-2">
          <summary className="text-[10px] text-slate-500 cursor-pointer">Audit trail ({approvals.length} events)</summary>
          <div className="mt-1 space-y-0.5 text-[11px] text-slate-600">
            {approvals.map((a, i) => (
              <div key={i}><span className="font-mono">{a.at?.slice(0,16)}</span> · <b>{a.action}</b> · {a.from} → {a.to} · by {a.by}{a.reason ? ` (${a.reason})` : ''}</div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function BlockTable({ blocks, editing, update, move, remove }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="pp-block-table">
      <table className="w-full text-xs">
        <thead className="bg-[#3b4872] text-white hover:bg-[#2d3856]">
          <tr>{[editing && '#', 'Code','Name','Start','End','Days','Wt %','Parent','Dependencies','Key Activities','Remarks', editing && ''].filter(Boolean).map((h,i) => <th key={i} className="text-left px-2 py-1.5 font-display font-bold uppercase tracking-widest text-[10px]">{h}</th>)}</tr>
        </thead>
        <tbody>
          {blocks.map((b, i) => (
            <tr key={b.id || i} className="border-b border-slate-100 hover:bg-slate-50 align-top">
              {editing && (
                <td className="px-1 py-1.5">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={()=>move(i,-1)} disabled={i===0} className="text-slate-400 hover:text-[#3b4872] disabled:opacity-30" data-testid={`pp-block-up-${i}`}><ArrowUp size={11} /></button>
                    <button onClick={()=>move(i,1)} disabled={i===blocks.length-1} className="text-slate-400 hover:text-[#3b4872] disabled:opacity-30" data-testid={`pp-block-down-${i}`}><ArrowDown size={11} /></button>
                  </div>
                </td>
              )}
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input value={b.code} onChange={e=>update(i,'code',e.target.value)} className="w-16 border border-slate-300 rounded-sm px-1 py-0.5" /> : b.code}</td>
              <td className="px-2 py-1.5 min-w-[200px]">{editing ? <input value={b.name} onChange={e=>update(i,'name',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : b.name}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="date" value={b.start_date||''} onChange={e=>update(i,'start_date',e.target.value)} className="border border-slate-300 rounded-sm px-1 py-0.5" /> : b.start_date}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="date" value={b.end_date||''} onChange={e=>update(i,'end_date',e.target.value)} className="border border-slate-300 rounded-sm px-1 py-0.5" /> : b.end_date}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="number" value={b.duration_days||0} onChange={e=>update(i,'duration_days',Number(e.target.value))} className="w-16 border border-slate-300 rounded-sm px-1 py-0.5 tabular-nums" /> : b.duration_days}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="number" step="0.1" value={b.weightage_pct||0} onChange={e=>update(i,'weightage_pct',Number(e.target.value))} className="w-16 border border-slate-300 rounded-sm px-1 py-0.5 tabular-nums" /> : b.weightage_pct}</td>
              <td className="px-2 py-1.5 font-mono">{editing ? <input value={b.parent_code||''} onChange={e=>update(i,'parent_code',e.target.value||null)} placeholder="—" className="w-16 border border-slate-300 rounded-sm px-1 py-0.5" /> : (b.parent_code || '—')}</td>
              <td className="px-2 py-1.5 text-slate-600 min-w-[160px]">{editing ? <input value={(b.dependencies||[]).join(', ')} onChange={e=>update(i,'dependencies',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} placeholder="comma-separated codes" className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : ((b.dependencies||[]).join(', ') || '—')}</td>
              <td className="px-2 py-1.5 text-slate-600 min-w-[260px]">{editing ? <textarea value={(b.key_activities||[]).join('\n')} onChange={e=>update(i,'key_activities',e.target.value.split('\n').map(s=>s.trim()).filter(Boolean))} placeholder="One activity per line" rows={Math.min(5, Math.max(1,(b.key_activities||[]).length))} className="w-full border border-slate-300 rounded-sm px-1 py-0.5 text-[11px]" /> : ((b.key_activities||[]).join('; ') || '—')}</td>
              <td className="px-2 py-1.5 text-slate-600 min-w-[140px]">{editing ? <input value={b.remarks||''} onChange={e=>update(i,'remarks',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : (b.remarks || '—')}</td>
              {editing && (
                <td className="px-1 py-1.5">
                  <button onClick={()=>remove(i)} className="text-slate-400 hover:text-rose-600" data-testid={`pp-block-rm-${i}`}><Trash2 size={12} /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MicroTable({ tasks, blocks, editing, update, move, remove }) {
  const blockMap = useMemo(() => Object.fromEntries((blocks || []).map(b => [b.code, b.name])), [blocks]);
  const blockOptions = (blocks || []).map(b => b.code);
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto max-h-[600px] overflow-y-auto" data-testid="pp-micro-table">
      <table className="w-full text-xs">
        <thead className="bg-[#3b4872] text-white hover:bg-[#2d3856] sticky top-0">
          <tr>{[editing && '#','Block','Code','Task','Start','End','Days','Category','Resources','BOQ','Deps','Remarks', editing && ''].filter(Boolean).map((h,i) => <th key={i} className="text-left px-2 py-1.5 font-display font-bold uppercase tracking-widest text-[10px]">{h}</th>)}</tr>
        </thead>
        <tbody>
          {tasks.map((t, i) => (
            <tr key={t.id || i} className="border-b border-slate-100 hover:bg-slate-50 align-top">
              {editing && (
                <td className="px-1 py-1.5">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={()=>move(i,-1)} disabled={i===0} className="text-slate-400 hover:text-[#3b4872] disabled:opacity-30"><ArrowUp size={11} /></button>
                    <button onClick={()=>move(i,1)} disabled={i===tasks.length-1} className="text-slate-400 hover:text-[#3b4872] disabled:opacity-30"><ArrowDown size={11} /></button>
                  </div>
                </td>
              )}
              <td className="px-2 py-1.5 text-slate-600 truncate max-w-[140px]">
                {editing ? (
                  <select value={t.block_code||''} onChange={e=>update(i,'block_code',e.target.value)} className="border border-slate-300 rounded-sm px-1 py-0.5 text-[11px] w-full">
                    {blockOptions.map(c=><option key={c} value={c}>{c} · {blockMap[c]||''}</option>)}
                  </select>
                ) : (blockMap[t.block_code] || t.block_code)}
              </td>
              <td className="px-2 py-1.5 font-mono">{editing ? <input value={t.code||''} onChange={e=>update(i,'code',e.target.value)} className="w-16 border border-slate-300 rounded-sm px-1 py-0.5" /> : t.code}</td>
              <td className="px-2 py-1.5 min-w-[220px]">{editing ? <input value={t.name||''} onChange={e=>update(i,'name',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : t.name}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="date" value={t.start_date||''} onChange={e=>update(i,'start_date',e.target.value)} className="border border-slate-300 rounded-sm px-1 py-0.5" /> : t.start_date}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="date" value={t.end_date||''} onChange={e=>update(i,'end_date',e.target.value)} className="border border-slate-300 rounded-sm px-1 py-0.5" /> : t.end_date}</td>
              <td className="px-2 py-1.5 font-mono tabular-nums">{editing ? <input type="number" value={t.duration_days||0} onChange={e=>update(i,'duration_days',Number(e.target.value))} className="w-14 border border-slate-300 rounded-sm px-1 py-0.5 tabular-nums" /> : t.duration_days}</td>
              <td className="px-2 py-1.5 text-slate-600">{editing ? <input value={t.category||''} onChange={e=>update(i,'category',e.target.value)} className="w-20 border border-slate-300 rounded-sm px-1 py-0.5" /> : (t.category || '—')}</td>
              <td className="px-2 py-1.5 text-slate-600 min-w-[140px]">{editing ? <input value={t.resources||''} onChange={e=>update(i,'resources',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : (t.resources || '—')}</td>
              <td className="px-2 py-1.5 font-mono">{editing ? <input value={t.boq_ref||''} onChange={e=>update(i,'boq_ref',e.target.value||null)} className="w-20 border border-slate-300 rounded-sm px-1 py-0.5" /> : (t.boq_ref || '—')}</td>
              <td className="px-2 py-1.5 text-slate-600 min-w-[120px]">{editing ? <input value={(t.dependencies||[]).join(', ')} onChange={e=>update(i,'dependencies',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} placeholder="comma codes" className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : ((t.dependencies||[]).join(', ') || '—')}</td>
              <td className="px-2 py-1.5 text-slate-600 min-w-[140px]">{editing ? <input value={t.remarks||''} onChange={e=>update(i,'remarks',e.target.value)} className="w-full border border-slate-300 rounded-sm px-1 py-0.5" /> : (t.remarks || '—')}</td>
              {editing && (
                <td className="px-1 py-1.5">
                  <button onClick={()=>remove(i)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GanttView({ items, title, editable, onChange }) {
  // Drag-and-drop Gantt: drag the bar body to shift both dates, drag edges to resize.
  const containerRef = useRef(null);
  const range = useMemo(() => {
    const dates = items.flatMap(i => [i.start_date, i.end_date]).filter(Boolean);
    if (!dates.length) return null;
    const min = dates.reduce((a, b) => a < b ? a : b);
    const max = dates.reduce((a, b) => a > b ? a : b);
    const minD = new Date(min); const maxD = new Date(max);
    const total = Math.max(1, (maxD - minD) / 86400000);
    return { min, max, minD, maxD, total };
  }, [items]);
  if (!range) return null;

  const isoAddDays = (iso, days) => {
    const d = new Date(iso); d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const startDrag = (e, idx, mode) => {
    if (!editable || !onChange) return;
    e.preventDefault();
    const track = e.currentTarget.parentElement;
    const trackRect = track.getBoundingClientRect();
    const pxPerDay = trackRect.width / range.total;
    const startX = e.clientX;
    const item = items[idx];
    const origStart = item.start_date;
    const origEnd = item.end_date;
    const onMove = (ev) => {
      const deltaDays = Math.round((ev.clientX - startX) / pxPerDay);
      if (deltaDays === 0) return;
      if (mode === 'move') {
        onChange(idx, { start_date: isoAddDays(origStart, deltaDays), end_date: isoAddDays(origEnd, deltaDays) });
      } else if (mode === 'start') {
        const newStart = isoAddDays(origStart, deltaDays);
        if (newStart < origEnd) onChange(idx, { start_date: newStart, duration_days: Math.max(1, Math.round((new Date(origEnd) - new Date(newStart))/86400000)) });
      } else if (mode === 'end') {
        const newEnd = isoAddDays(origEnd, deltaDays);
        if (newEnd > origStart) onChange(idx, { end_date: newEnd, duration_days: Math.max(1, Math.round((new Date(newEnd) - new Date(origStart))/86400000)) });
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3" data-testid="pp-gantt">
      <div className="kpi-overline mb-2 flex items-center justify-between">
        <span>{title} · {range.min} → {range.max}</span>
        {editable && <span className="text-[10px] text-amber-700 font-normal normal-case">drag bars to shift · drag edges to resize</span>}
      </div>
      <div ref={containerRef} className="space-y-1 max-h-[400px] overflow-y-auto">
        {items.map((it, i) => {
          if (!it.start_date || !it.end_date) return null;
          const s = (new Date(it.start_date) - range.minD) / 86400000;
          const e = (new Date(it.end_date) - range.minD) / 86400000;
          const left = (s / range.total) * 100;
          const width = Math.max(0.5, ((e - s) / range.total) * 100);
          return (
            <div key={it.id || i} className="flex items-center gap-2 text-[10px]">
              <span className="w-40 truncate font-display font-semibold text-slate-700">{it.code ? `${it.code} · ` : ''}{it.name}</span>
              <div className="flex-1 relative h-5 bg-slate-50 rounded-sm">
                <div
                  className={`absolute h-4 top-0.5 bg-[#3b4872] rounded-sm flex ${editable ? 'cursor-move' : ''}`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                  onMouseDown={editable ? (e) => startDrag(e, i, 'move') : undefined}
                  title={`${it.start_date} → ${it.end_date} (${it.duration_days}d)`}
                >
                  {editable && width > 4 && (
                    <>
                      <div onMouseDown={(e) => { e.stopPropagation(); startDrag(e, i, 'start'); }} className="w-1.5 h-full bg-white/50 cursor-ew-resize rounded-l-sm hover:bg-white" />
                      <div className="flex-1" />
                      <div onMouseDown={(e) => { e.stopPropagation(); startDrag(e, i, 'end'); }} className="w-1.5 h-full bg-white/50 cursor-ew-resize rounded-r-sm hover:bg-white" />
                    </>
                  )}
                </div>
              </div>
              <span className="w-12 text-right tabular-nums text-slate-500">{it.duration_days}d</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RejectModal({ reason, setReason, onCancel, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div onClick={e=>e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-3" data-testid="pp-reject-modal">
        <h3 className="font-display font-bold text-base">Reason for rejection</h3>
        <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3} maxLength={500} placeholder="Why is this being sent back?" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" autoFocus />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm">Cancel</button>
          <button onClick={onSubmit} data-testid="pp-reject-submit" className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-sm font-display font-semibold">Reject & Send Back</button>
        </div>
      </div>
    </div>
  );
}
